// src/components/negocios/NegocioFormModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import * as ReactIMask from 'react-imask';
import { TIPOS_IMOVEL, OBJETIVOS_BUSCA } from '../../constants/negocios';

import {
  Home,
  Building2,
  MapPinned,
  Trees,
  Tractor,
  Warehouse,
  Check,
  Info,
} from 'lucide-react';

/**
 * ✅ Compat total com react-imask (evita quebra em build/prod)
 * - alguns builds exportam { IMaskInput }
 * - outros exportam default
 * - outros exportam default.IMaskInput
 */
const MaskInput =
  ReactIMask?.IMaskInput ||
  ReactIMask?.default?.IMaskInput ||
  ReactIMask?.default;

const capitalizeWords = (text = '') =>
  String(text || '')
    .trim()
    .replace(/\s+/g, ' ')
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');

const toUpperClean = (text = '') =>
  String(text || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();

const toNumber = (val) => {
  if (val === null || val === undefined || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
};

function normalizeText(s = '') {
  return String(s || '')
    .trim()
    .replace(/\s+/g, ' ')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function parseCSV(text = '') {
  return String(text || '')
    .split(',')
    .map((x) => normalizeText(x))
    .map((x) => x.trim())
    .filter(Boolean);
}

/**
 * Flexibilidade v1
 * - alta: sem bairros OU regiao ampla OU >=4 bairros
 * - media: 2-3 bairros
 * - baixa: 1 bairro
 */
function calcFlexFromForm({ bairrosText = '', regiao = '' }) {
  const bairros = parseCSV(bairrosText);
  const reg = normalizeText(regiao).toLowerCase();

  const regiaoAmpla =
    reg.includes('qualquer') ||
    reg.includes('toda') ||
    reg.includes('geral') ||
    reg.includes('sinop') ||
    reg.includes('centro') ||
    false;

  if (bairros.length === 0) return 'alta';
  if (regiaoAmpla) return 'alta';
  if (bairros.length >= 4) return 'alta';
  if (bairros.length >= 2) return 'media';
  return 'baixa';
}

export default function NegocioFormModal({
  open,
  onClose,
  onSave,
  initialData,
}) {
  const PERMUTA_OPTS = useMemo(
    () => [
      { key: 'casa', label: 'Casa', Icon: Home },
      { key: 'apartamento', label: 'Apartamento', Icon: Building2 },
      { key: 'terreno', label: 'Terreno', Icon: MapPinned },
      { key: 'chacara', label: 'Chácara', Icon: Trees },
      { key: 'fazenda', label: 'Fazenda', Icon: Tractor },
      { key: 'barracao', label: 'Barracão', Icon: Warehouse },
    ],
    []
  );

  const emptyPermutaTipos = useMemo(
    () => ({
      casa: { ativo: false, valor: '' },
      apartamento: { ativo: false, valor: '' },
      terreno: { ativo: false, valor: '' },
      chacara: { ativo: false, valor: '' },
      fazenda: { ativo: false, valor: '' },
      barracao: { ativo: false, valor: '' },
    }),
    []
  );

  const [form, setForm] = useState({
    tituloNegocio: '',
    nomeCliente: '',
    objetivo: '',
    tipoImovel: '',
    valorMin: '',
    valorMax: '',
    cidade: '',
    bairrosText: '',
    regiao: '',
    observacoes: '',
    prioridade: 'media',
    visibilidade: 'privado',
    statusPipeline: 'contato',
    whatsapp: '',
    aceitaPermuta: false,
    permutaTipos: emptyPermutaTipos,
    percentualPermutaFaixa: '',
    itens_disponiveis_ids: [],
    entrada_disponivel: '',
  });

  // ✅ Hook SEMPRE antes de qualquer return condicional (corrige erro #310)
  const flexPreview = useMemo(() => {
    const lvl = calcFlexFromForm({
      bairrosText: form.bairrosText,
      regiao: form.regiao,
    });
    if (lvl === 'alta') return { level: 'alta', label: 'Alta flexibilidade' };
    if (lvl === 'media')
      return { level: 'media', label: 'Flexibilidade média' };
    return { level: 'baixa', label: 'Baixa flexibilidade' };
  }, [form.bairrosText, form.regiao]);

  useEffect(() => {
    if (!open) return;

    if (initialData) {
      const bairros = initialData.bairros || initialData.bairrosDesejados || [];
      const permuta = initialData.permuta || null;

      const aceitaPermuta =
        typeof permuta?.aceita === 'boolean'
          ? permuta.aceita
          : !!initialData.aceitaPermuta;

      let permutaTipos = { ...emptyPermutaTipos };
      if (Array.isArray(permuta?.itens)) {
        for (const it of permuta.itens) {
          const key = it?.key;
          if (key && permutaTipos[key]) {
            permutaTipos[key] = {
              ativo: true,
              valor:
                it?.valorAproximado !== null &&
                it?.valorAproximado !== undefined
                  ? String(it.valorAproximado)
                  : '',
            };
          }
        }
      }

      const obsUnified =
        initialData.observacoes_corretor ||
        initialData.descricao ||
        initialData.observacoes ||
        '';

      setForm((prev) => ({
        ...prev,
        tituloNegocio: initialData.tituloNegocio || '',
        nomeCliente:
          initialData.nomeCliente || initialData.clientePrimeiroNome || '',
        objetivo: initialData.objetivo || initialData.buscaTags?.[0] || '',
        tipoImovel: initialData.tipoImovel || '',

        valorMin:
          initialData.valorMin !== null && initialData.valorMin !== undefined
            ? String(initialData.valorMin)
            : '',
        valorMax:
          initialData.valorMax !== null && initialData.valorMax !== undefined
            ? String(initialData.valorMax)
            : '',

        cidade: initialData.cidade || initialData.cidadePrincipal || '',
        bairrosText: bairros.join(', '),
        regiao:
          initialData.regiao || initialData.zona || initialData.setor || '',
        observacoes: obsUnified,

        prioridade: initialData.prioridade || 'media',
        visibilidade: initialData.visibilidade || 'privado',
        statusPipeline: initialData.statusPipeline || 'contato',

        whatsapp: initialData.whatsapp || '',

        aceitaPermuta,
        permutaTipos,
        percentualPermutaFaixa:
          permuta?.faixa || initialData.percentualPermutaFaixa || '',

        itens_disponiveis_ids: Array.isArray(initialData.itens_disponiveis_ids)
          ? initialData.itens_disponiveis_ids
          : [],
        entrada_disponivel:
          initialData.entrada_disponivel !== null &&
          initialData.entrada_disponivel !== undefined
            ? String(initialData.entrada_disponivel)
            : '',
      }));
    } else {
      setForm({
        tituloNegocio: '',
        nomeCliente: '',
        objetivo: '',
        tipoImovel: '',
        valorMin: '',
        valorMax: '',
        cidade: '',
        bairrosText: '',
        regiao: '',
        observacoes: '',
        prioridade: 'media',
        visibilidade: 'privado',
        statusPipeline: 'contato',
        whatsapp: '',
        aceitaPermuta: false,
        permutaTipos: emptyPermutaTipos,
        percentualPermutaFaixa: '',
        itens_disponiveis_ids: [],
        entrada_disponivel: '',
      });
    }
  }, [open, initialData, emptyPermutaTipos]);

  // ✅ retorno condicional APÓS todos os hooks
  if (!open) return null;

  // ✅ se o react-imask não estiver disponível, evita quebrar a rota
  if (!MaskInput) {
    return (
      <ModalShell open={open} onClose={onClose}>
        <div className="w-full max-w-xl rounded-2xl border bg-white p-6 shadow-xl">
          <div className="text-lg font-semibold text-zinc-900">
            Erro de dependência
          </div>
          <p className="mt-2 text-sm text-zinc-600">
            O componente de máscara (react-imask) não carregou corretamente.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
            >
              Fechar
            </button>
          </div>
        </div>
      </ModalShell>
    );
  }

  const handleChange = (field) => (e) => {
    const value =
      e?.target?.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
  };

  const togglePermutaTipo = (key) => {
    setForm((prev) => {
      const ativoAtual = !!prev.permutaTipos?.[key]?.ativo;
      return {
        ...prev,
        permutaTipos: {
          ...prev.permutaTipos,
          [key]: {
            ...prev.permutaTipos[key],
            ativo: !ativoAtual,
            valor: ativoAtual ? '' : prev.permutaTipos[key]?.valor || '',
          },
        },
      };
    });
  };

  const setPermutaValor = (key, valor) => {
    setForm((prev) => ({
      ...prev,
      permutaTipos: {
        ...prev.permutaTipos,
        [key]: { ...prev.permutaTipos[key], valor },
      },
    }));
  };

  const getPermutaItensPayload = () => {
    const itens = [];
    for (const opt of PERMUTA_OPTS) {
      const item = form.permutaTipos?.[opt.key];
      if (item?.ativo) {
        itens.push({
          tipo: opt.label,
          key: opt.key,
          valorAproximado: toNumber(item.valor) || 0,
        });
      }
    }
    return itens;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const bairros = parseCSV(form.bairrosText);
    const bairrosSlug = bairros.map((b) =>
      b
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
    );

    if (form.aceitaPermuta) {
      const itens = getPermutaItensPayload();
      if (!itens.length)
        return alert('Selecione pelo menos 1 tipo de permuta.');
      const faltandoValor = itens.find(
        (i) => !i.valorAproximado || i.valorAproximado <= 0
      );
      if (faltandoValor)
        return alert(
          'Informe o valor aproximado do bem de permuta selecionado.'
        );
      if (!form.percentualPermutaFaixa)
        return alert('Selecione o percentual de permuta aceito.');
    }

    const permutaItens = form.aceitaPermuta ? getPermutaItensPayload() : [];
    const permutaFaixa = form.aceitaPermuta ? form.percentualPermutaFaixa : '';
    const permutaNegociavel = permutaFaixa === 'negociavel';

    const obs = String(form.observacoes || '').trim();
    const tituloNegocioUpper = toUpperClean(form.tituloNegocio);

    const flexLevel = calcFlexFromForm({
      bairrosText: form.bairrosText,
      regiao: form.regiao,
    });

    onSave({
      tituloNegocio: tituloNegocioUpper,

      nomeCliente: capitalizeWords(form.nomeCliente),
      clientePrimeiroNome: capitalizeWords(form.nomeCliente),

      objetivo: form.objetivo,
      buscaTags: form.objetivo ? [form.objetivo] : [],

      tipoImovel: form.tipoImovel,

      valorMin: form.valorMin ? Number(form.valorMin) : null,
      valorMax: form.valorMax ? Number(form.valorMax) : null,

      cidade: capitalizeWords(form.cidade),
      cidadePrincipal: capitalizeWords(form.cidade),

      bairros,
      bairrosDesejados: bairros,
      bairrosSlug,

      regiao: normalizeText(form.regiao),
      flexibilidadeNivel: flexLevel,

      aceitaPermuta: !!form.aceitaPermuta,
      permuta: {
        aceita: !!form.aceitaPermuta,
        itens: permutaItens,
        faixa: permutaFaixa,
        negociavel: permutaNegociavel,
      },

      itens_disponiveis_ids: Array.isArray(form.itens_disponiveis_ids)
        ? form.itens_disponiveis_ids
        : [],
      entrada_disponivel: form.entrada_disponivel
        ? Number(form.entrada_disponivel)
        : null,

      observacoes_corretor: obs,
      descricao: obs,

      prioridade: form.prioridade,
      visibilidade: form.visibilidade,
      statusPipeline: form.statusPipeline,

      whatsapp: String(form.whatsapp || '').trim(),

      ...(initialData?.origem ? { origem: initialData.origem } : {}),
      ...(initialData?.upgradeImovelId
        ? { upgradeImovelId: initialData.upgradeImovelId }
        : {}),
      ...(initialData?.imovelOrigemId
        ? { imovelOrigemId: initialData.imovelOrigemId }
        : {}),
      ...(initialData?.permutaImovelId
        ? { permutaImovelId: initialData.permutaImovelId }
        : {}),
    });
  };

  return (
    <ModalShell open={open} onClose={onClose}>
      <div className="w-full max-w-4xl rounded-2xl border bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div className="text-lg font-semibold text-zinc-900">
            {initialData ? 'Editar Negócio' : 'Novo Negócio'}
          </div>
          <button
            onClick={onClose}
            className="text-sm text-zinc-500 hover:text-zinc-800"
            type="button"
          >
            Fechar
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col">
          <div className="max-h-[75vh] overflow-y-auto px-5 py-4 space-y-4 text-sm">
            {/* Título */}
            <div>
              <label className="text-xs font-medium text-zinc-600">
                Título do negócio
              </label>
              <input
                value={form.tituloNegocio}
                onChange={handleChange('tituloNegocio')}
                onBlur={() =>
                  setForm((f) => ({
                    ...f,
                    tituloNegocio: toUpperClean(f.tituloNegocio),
                  }))
                }
                className="mt-1 w-full rounded-xl border px-3 py-2 font-semibold uppercase tracking-wide"
                placeholder="EX.: APARTAMENTO ALTO PADRÃO COM VOLTA"
              />
              <div className="mt-1 text-[11px] text-zinc-500">
                Esse texto aparece em destaque no card (caixa alta).
              </div>
            </div>

            {/* Dados principais */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-zinc-600">
                  Nome do cliente
                </label>
                <input
                  value={form.nomeCliente}
                  onChange={handleChange('nomeCliente')}
                  onBlur={() =>
                    setForm((f) => ({
                      ...f,
                      nomeCliente: capitalizeWords(f.nomeCliente),
                    }))
                  }
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  placeholder="Ex.: João Silva"
                  required
                />
              </div>

              <div>
                <label className="text-xs font-medium text-zinc-600">
                  O que ele busca?
                </label>
                <select
                  value={form.objetivo}
                  onChange={handleChange('objetivo')}
                  className="mt-1 w-full rounded-xl border bg-white px-3 py-2"
                  required
                >
                  <option value="">Selecione...</option>
                  {(OBJETIVOS_BUSCA || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Tipo e valores */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-zinc-600">
                  Tipo de imóvel
                </label>
                <select
                  value={form.tipoImovel}
                  onChange={handleChange('tipoImovel')}
                  className="mt-1 w-full rounded-xl border bg-white px-3 py-2"
                  required
                >
                  <option value="">Selecione...</option>
                  {(TIPOS_IMOVEL || []).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-zinc-600">
                    Valor mín. (R$)
                  </label>
                  <MaskInput
                    mask="R$ num"
                    blocks={{
                      num: {
                        mask: Number,
                        thousandsSeparator: '.',
                        radix: ',',
                        scale: 2,
                        mapToRadix: [','],
                        padFractionalZeros: true,
                        normalizeZeros: true,
                        signed: false,
                      },
                    }}
                    unmask={true}
                    value={form.valorMin}
                    onAccept={(v) => setForm((f) => ({ ...f, valorMin: v }))}
                    className="mt-1 w-full rounded-xl border px-3 py-2"
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-zinc-600">
                    Valor máx. (R$)
                  </label>
                  <MaskInput
                    mask="R$ num"
                    blocks={{
                      num: {
                        mask: Number,
                        thousandsSeparator: '.',
                        radix: ',',
                        scale: 2,
                        mapToRadix: [','],
                        padFractionalZeros: true,
                        normalizeZeros: true,
                        signed: false,
                      },
                    }}
                    unmask={true}
                    value={form.valorMax}
                    onAccept={(v) => setForm((f) => ({ ...f, valorMax: v }))}
                    className="mt-1 w-full rounded-xl border px-3 py-2"
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                  />
                </div>
              </div>
            </div>

            {/* Localização */}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="text-xs font-medium text-zinc-600">
                  Cidade
                </label>
                <input
                  value={form.cidade}
                  onChange={handleChange('cidade')}
                  onBlur={() =>
                    setForm((f) => ({
                      ...f,
                      cidade: capitalizeWords(f.cidade),
                    }))
                  }
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  placeholder="Ex.: Sinop - MT"
                />
              </div>

              <div>
                <label className="text-xs font-medium text-zinc-600">
                  Região (texto livre)
                </label>
                <input
                  value={form.regiao}
                  onChange={handleChange('regiao')}
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  placeholder="Ex.: Zona Leste, Próx. Av. X, Região do Aeroporto..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs font-medium text-zinc-600">
                  Bairros (separados por vírgula)
                </label>
                <input
                  value={form.bairrosText}
                  onChange={handleChange('bairrosText')}
                  className="mt-1 w-full rounded-xl border px-3 py-2"
                  placeholder="Centro, Flamboyants..."
                />
                <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500">
                  <Info className="h-3.5 w-3.5 text-zinc-400" />
                  <span>
                    Flexibilidade detectada: <b>{flexPreview.label}</b> (auto)
                  </span>
                </div>
              </div>
            </div>

            {/* Permuta */}
            <div className="rounded-2xl border border-zinc-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-zinc-900">
                    Permuta
                  </div>
                  <div className="text-xs text-zinc-500">
                    Preencha para melhorar o match da Máquina de Vendas.
                  </div>
                </div>

                <label className="inline-flex items-center gap-2 text-xs text-zinc-700">
                  <input
                    type="checkbox"
                    checked={form.aceitaPermuta}
                    onChange={(e) => {
                      const v = e.target.checked;
                      setForm((f) => ({
                        ...f,
                        aceitaPermuta: v,
                        percentualPermutaFaixa: v
                          ? f.percentualPermutaFaixa
                          : '',
                        permutaTipos: v ? f.permutaTipos : emptyPermutaTipos,
                      }));
                    }}
                  />
                  Negócio com permuta
                </label>
              </div>

              {form.aceitaPermuta && (
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="text-xs font-medium text-zinc-600">
                      Tipos de permuta que o cliente aceita
                    </label>

                    <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {PERMUTA_OPTS.map(({ key, label, Icon }) => {
                        const ativo = !!form.permutaTipos?.[key]?.ativo;

                        return (
                          <button
                            key={key}
                            type="button"
                            onClick={() => togglePermutaTipo(key)}
                            className={[
                              'w-full p-4 rounded-xl border transition-colors text-left',
                              ativo
                                ? 'border-emerald-300 bg-emerald-50'
                                : 'border-gray-200 bg-white hover:bg-gray-50',
                            ].join(' ')}
                          >
                            <div className="flex items-center gap-3">
                              <span
                                className={[
                                  'h-9 w-9 rounded-lg flex items-center justify-center',
                                  ativo
                                    ? 'bg-emerald-600 text-white'
                                    : 'bg-gray-100 text-gray-600',
                                ].join(' ')}
                              >
                                <Icon size={18} />
                              </span>

                              <div className="flex-1">
                                <div className="text-sm font-semibold text-gray-900">
                                  {label}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {ativo
                                    ? 'Selecionado'
                                    : 'Clique para selecionar'}
                                </div>
                              </div>

                              {ativo && (
                                <span className="h-7 w-7 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                                  <Check size={16} />
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-zinc-600">
                      Valor aproximado do bem (por tipo selecionado)
                    </label>

                    <div className="mt-2 grid grid-cols-1 gap-4 md:grid-cols-2">
                      {PERMUTA_OPTS.filter(
                        (o) => form.permutaTipos?.[o.key]?.ativo
                      ).map(({ key, label, Icon }) => (
                        <div
                          key={key}
                          className="p-4 rounded-xl border border-gray-200 bg-white"
                        >
                          <div className="mb-2 flex items-center gap-2">
                            <span className="h-8 w-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center">
                              <Icon size={16} />
                            </span>
                            <span className="text-sm font-medium text-gray-700">
                              {label}
                            </span>
                          </div>

                          <MaskInput
                            mask="R$ num"
                            blocks={{
                              num: {
                                mask: Number,
                                thousandsSeparator: '.',
                                radix: ',',
                                scale: 2,
                                mapToRadix: [','],
                                padFractionalZeros: true,
                                normalizeZeros: true,
                                signed: false,
                              },
                            }}
                            unmask={true}
                            placeholder="R$ 0,00"
                            value={form.permutaTipos?.[key]?.valor || ''}
                            onAccept={(v) => setPermutaValor(key, v)}
                            className="mt-1 w-full rounded-xl border px-3 py-2"
                            inputMode="numeric"
                          />
                        </div>
                      ))}

                      {!PERMUTA_OPTS.some(
                        (o) => form.permutaTipos?.[o.key]?.ativo
                      ) && (
                        <div className="text-sm text-gray-500">
                          Selecione pelo menos um tipo de permuta acima para
                          informar o valor.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-medium text-zinc-600">
                        Qual percentual de permuta aceita?
                      </label>
                      <select
                        value={form.percentualPermutaFaixa}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            percentualPermutaFaixa: e.target.value,
                          }))
                        }
                        className="mt-1 w-full rounded-xl border bg-white px-3 py-2"
                      >
                        <option value="" disabled>
                          Selecione
                        </option>
                        <option value="30-50">30–50% — Saudável</option>
                        <option value="50-90">50–90% — Venda rápida</option>
                        <option value="negociavel">
                          Permuta negociável — aberto a negociação
                        </option>
                      </select>
                    </div>

                    <div className="flex items-center text-xs text-zinc-500">
                      Evita propostas fora do padrão e melhora o match
                      automático.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Entrada disponível */}
            <div className="rounded-2xl border border-zinc-200 p-4">
              <div>
                <div className="text-sm font-semibold text-zinc-900">
                  Entrada disponível
                </div>
                <div className="text-xs text-zinc-500">
                  Se o cliente completa com dinheiro, isso ajuda a engine a
                  fechar VERDE.
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-medium text-zinc-600">
                    Valor de entrada (R$) — opcional
                  </label>
                  <MaskInput
                    mask="R$ num"
                    blocks={{
                      num: {
                        mask: Number,
                        thousandsSeparator: '.',
                        radix: ',',
                        scale: 2,
                        mapToRadix: [','],
                        padFractionalZeros: true,
                        normalizeZeros: true,
                        signed: false,
                      },
                    }}
                    unmask={true}
                    value={form.entrada_disponivel}
                    onAccept={(v) =>
                      setForm((f) => ({ ...f, entrada_disponivel: v }))
                    }
                    className="mt-1 w-full rounded-xl border px-3 py-2"
                    placeholder="R$ 0,00"
                    inputMode="numeric"
                  />
                </div>

                <div className="flex items-center text-xs text-zinc-500">
                  (Seleção de ativos por ID entra na próxima etapa — com
                  picker.)
                </div>
              </div>
            </div>

            {/* Contato */}
            <div>
              <label className="text-xs font-medium text-zinc-600">
                WhatsApp
              </label>
              <MaskInput
                mask={[{ mask: '(00) 00000-0000' }, { mask: '(00) 0000-0000' }]}
                unmask={true}
                value={form.whatsapp}
                onAccept={(v) => setForm((f) => ({ ...f, whatsapp: v }))}
                className="mt-1 w-full rounded-xl border px-3 py-2"
                placeholder="(66) 99999-9999"
                inputMode="tel"
              />
            </div>

            {/* Observações */}
            <div>
              <label className="text-xs font-medium text-zinc-600">
                Observações do negócio
              </label>
              <textarea
                value={form.observacoes}
                onChange={handleChange('observacoes')}
                rows={4}
                className="mt-1 w-full rounded-xl border px-3 py-2"
                placeholder="Ex.: cliente quer condomínio fechado; aceita permuta até 50%; precisa mudar em 60 dias; prefere Jardim Maringá..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t bg-white px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            >
              Salvar negócio
            </button>
          </div>
        </form>
      </div>
    </ModalShell>
  );
}

/**
 * ModalShell:
 * - rolagem do overlay
 * - trava scroll do body
 * - fecha ao clicar fora
 */
function ModalShell({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev || '';
    };
  }, [open]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4 md:p-6">
      <div className="min-h-full w-full flex items-start justify-center">
        <div
          className="my-6 w-full flex justify-center"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
